import React, {
  ChangeEvent,
  FC,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { CustomFormulaValues } from 'src/components/Forms/CustomFormulaForm';
import {
  CollapsesContainer,
  MetricsCard,
  MetricsCardTitle,
} from 'src/components/Forms/CustomFormulaForm/Metrics/styled';
import { SearchInput } from 'src/components/Forms/FormInputs/SearchInput';
import { MetricsDropdown } from 'src/components/Forms/MetricsDropdown';
import { ReactComponent as FacebookAdsIcon } from 'src/icons/facebook.svg';
import { ReactComponent as GoogleAdsIcon } from 'src/icons/googleAds.svg';
import { ReactComponent as GoogleAnalyticsIcon } from 'src/icons/googleAnalytics.svg';
import { ReactComponent as ShopifyIcon } from 'src/icons/shopify.svg';
import { ReactComponent as MagentoIcon } from 'src/icons/magento.svg';
import kipifyLogo from 'src/icons/kipify-logo.png';
import { groupMetricsByCategory } from 'src/utils';
import {
  ConnectorType,
  CustomFormulaItemType,
  Metric,
  MetricsByConnectorType,
} from 'src/utils/gql/types';
import { v4 as uuidv4 } from 'uuid';

export const filterMetrics = (search: string, metrics: Metric[] = []) => {
  const normalizedSearch = search.trim().toLowerCase();
  if (!normalizedSearch) return metrics;

  return metrics.filter(
    (metric) =>
      metric.label.toLowerCase().includes(normalizedSearch) ||
      metric.key.toLowerCase().includes(normalizedSearch),
  );
};
export const filterCustomFormulaMetric = (
  search: string,
  metrics: { id: string; name: string }[] = [],
) => {
  return metrics.filter((metric) =>
    metric.name.toLowerCase().includes(search.toLowerCase()),
  );
};

interface ConnectorData {
  metrics: Metric[];
  loading: boolean;
}

interface MetricsProps {
  facebookData: ConnectorData;
  googleAdsData: ConnectorData;
  googleAnalyticsData: ConnectorData;
  shopifyData: ConnectorData;
  magentoData: ConnectorData;
  databaseData?: ConnectorData;
  expandedCollapseType: ConnectorType | null | MetricsByConnectorType;
  onToggleCollapse: (type: ConnectorType | MetricsByConnectorType) => void;
}

export const Metrics: FC<MetricsProps> = ({
  facebookData,
  googleAdsData,
  googleAnalyticsData,
  shopifyData,
  magentoData,
  databaseData,
  expandedCollapseType,
  onToggleCollapse,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const metricsContainerRef = useRef<HTMLDivElement | null>(null);
  const collapsesRef = useRef<Map<string, HTMLDivElement>>(new Map());

  const [metricsContainerMinHeight, setMetricsContainerMinHeight] = useState(0);
  const [search, setSearch] = useState('');

  const { setValue, getValues, trigger } =
    useFormContext<CustomFormulaValues>();

  const formulaItems = getValues('formulaItems');
  const chosenMetrics = formulaItems.filter(
    (item) => item.type === CustomFormulaItemType.metric,
  );

  const handleChangeSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const clearSearch = () => setSearch('');

  const handleClickMetric = (metric: Metric) => {
    const updatedFormulaItems = [
      ...formulaItems,
      {
        value: metric.key,
        type: CustomFormulaItemType.metric,
        label: metric.label,
        connectorType: metric.type,
        metricId: metric.id,
        width: 0,
        id: uuidv4(),
      },
    ];

    setValue('formulaItems', updatedFormulaItems, { shouldValidate: true });
    trigger('formulaItems');
  };

  useEffect(() => {
    if (metricsContainerRef.current && !isSmallScreen) {
      setMetricsContainerMinHeight(metricsContainerRef.current.offsetHeight);
    }
  }, [isSmallScreen]);

  useEffect(() => {
    const adjustCollapseContentHeights = () => {
      if (
        collapsesRef.current &&
        metricsContainerRef.current &&
        metricsContainerRef.current.parentElement
      ) {
        const collapses = Array.from(collapsesRef.current.values());
        const expandedCollapse = collapses.find(
          (collapse) => collapse.getAttribute('data-expanded') === 'true',
        );
        const parentContainerHeight =
          metricsContainerRef.current.parentElement.clientHeight;
        const styles = window.getComputedStyle(metricsContainerRef.current);
        const borderTop = parseFloat(styles.borderTop);
        const borderBottom = parseFloat(styles.borderBottom);

        if (expandedCollapse) {
          expandedCollapse.style.maxHeight = `${Math.max(parentContainerHeight - metricsContainerMinHeight - borderBottom - borderTop)}px`;
        }
      }
    };

    if (!isSmallScreen) {
      adjustCollapseContentHeights();
    }
  }, [expandedCollapseType, isSmallScreen, metricsContainerMinHeight]);

  // temporary code

  const preFilteredMetrics = useMemo(
    () =>
      [...googleAdsData.metrics].sort((a, b) => a.label.localeCompare(b.label)),
    [googleAdsData.metrics],
  );
  /// /

  const memoizedMetrics = useMemo(() => {
    const filteredFacebookMetrics = filterMetrics(search, facebookData.metrics);
    const filteredGoogleAdsMetrics = filterMetrics(search, preFilteredMetrics);
    const filteredGoogleAnalyticsMetrics = filterMetrics(
      search,
      googleAnalyticsData.metrics.filter(
        (metric) =>
          metric.label !== 'Event name' && metric.key !== 'event_name',
      ),
    );
    const filteredShopifyMetrics = filterMetrics(
      search,
      shopifyData.metrics.filter((metric) => metric.label !== 'Customers'),
    );
    const filteredMagentoMetrics = filterMetrics(search, magentoData.metrics);

    // Metriche Kipify hardcoded
    const kipifyMetrics: Metric[] = [
      {
        id: 'kipify_total_marketing_spend',
        key: 'total_marketing_spend',
        label: 'Total Marketing Spend',
        category: 'metric',
        type: MetricsByConnectorType.KIPIFY,
        valueType: 'currency' as any,
      },
      {
        id: 'kipify_markup',
        key: 'markup',
        label: 'Markup',
        category: 'metric',
        type: MetricsByConnectorType.KIPIFY,
        valueType: 'number' as any,
      },
      {
        id: 'kipify_roas',
        key: 'roas',
        label: 'Roas',
        category: 'metric',
        type: MetricsByConnectorType.KIPIFY,
        valueType: 'number' as any,
      },
      {
        id: 'kipify_acos',
        key: 'acos',
        label: 'ACOS',
        category: 'metric',
        type: MetricsByConnectorType.KIPIFY,
        valueType: 'percentage' as any,
      },
      {
        id: 'kipify_acos_total',
        key: 'acos_total',
        label: 'ACOS (shipping incl.)',
        category: 'metric',
        type: MetricsByConnectorType.KIPIFY,
        valueType: 'percentage' as any,
      },
      {
        id: 'kipify_oac',
        key: 'oac',
        label: 'OAC',
        category: 'metric',
        type: MetricsByConnectorType.KIPIFY,
        valueType: 'currency' as any,
      },
      {
        id: 'kipify_cac',
        key: 'cac',
        label: 'CAC',
        category: 'metric',
        type: MetricsByConnectorType.KIPIFY,
        valueType: 'currency' as any,
      },
      {
        id: 'kipify_cac_new_customers',
        key: 'cac_new_customers',
        label: 'CAC (New Customers)',
        category: 'metric',
        type: MetricsByConnectorType.KIPIFY,
        valueType: 'currency' as any,
      },
    ];
    const filteredKipifyMetrics = filterMetrics(search, kipifyMetrics);

    return {
      facebookMetrics: groupMetricsByCategory(filteredFacebookMetrics),
      googleAdsMetrics: groupMetricsByCategory(filteredGoogleAdsMetrics),
      googleAnalyticsMetrics: groupMetricsByCategory(
        filteredGoogleAnalyticsMetrics,
      ),
      shopifyMetrics: groupMetricsByCategory(filteredShopifyMetrics),
      magentoMetrics: groupMetricsByCategory(filteredMagentoMetrics),
      kipifyMetrics: groupMetricsByCategory(filteredKipifyMetrics),
      databaseMetrics: groupMetricsByCategory(
        filterMetrics(search, databaseData?.metrics || []),
      ),
    };
  }, [
    search,
    facebookData.metrics,
    preFilteredMetrics,
    googleAnalyticsData.metrics,
    shopifyData.metrics,
    magentoData.metrics,
    databaseData?.metrics,
  ]);

  const renderMetricsDropdown = (
    type: ConnectorType | MetricsByConnectorType,
    label: string,
    icon: JSX.Element,
    metrics: Record<string, Metric[]>,
    loading: boolean,
    totalMetricsCount = 0,
  ) => (
    <MetricsDropdown
      type={type}
      label={label}
      icon={icon}
      expanded={expandedCollapseType === type}
      metricsByCategory={metrics}
      chosenMetrics={chosenMetrics}
      loading={loading}
      totalMetricsCount={totalMetricsCount}
      searchQuery={search}
      onClickMetric={handleClickMetric}
      onToggle={onToggleCollapse}
      ref={(node) => {
        if (node) {
          collapsesRef.current.set(type, node);
        } else {
          collapsesRef.current.delete(type);
        }
      }}
    />
  );

  return (
    <MetricsCard ref={metricsContainerRef}>
      <MetricsCardTitle>Metrics</MetricsCardTitle>
      <SearchInput
        placeholder="Find a metric"
        name="search"
        value={search}
        onDelete={clearSearch}
        onChange={handleChangeSearch}
      />
      <CollapsesContainer>
        {renderMetricsDropdown(
          MetricsByConnectorType.KIPIFY,
          'Kipify',
          <img
            src={kipifyLogo}
            alt="Kipify"
            style={{ width: '20px', height: '20px' }}
          />,
          memoizedMetrics.kipifyMetrics,
          false,
        )}
        {renderMetricsDropdown(
          ConnectorType.SHOPIFY,
          'Shopify',
          <ShopifyIcon />,
          memoizedMetrics.shopifyMetrics,
          shopifyData.loading,
        )}
        {renderMetricsDropdown(
          ConnectorType.MAGENTO,
          'Magento',
          <MagentoIcon />,
          memoizedMetrics.magentoMetrics,
          magentoData.loading,
        )}
        {renderMetricsDropdown(
          ConnectorType.GOOGlE_ANALYTICS4,
          'Google Analytics 4',
          <GoogleAnalyticsIcon />,
          memoizedMetrics.googleAnalyticsMetrics,
          googleAnalyticsData.loading,
        )}
        {renderMetricsDropdown(
          ConnectorType.GOOGLE_ADS,
          'Google Ads',
          <GoogleAdsIcon />,
          memoizedMetrics.googleAdsMetrics,
          googleAdsData.loading,
        )}
        {renderMetricsDropdown(
          ConnectorType.FACEBOOK,
          'Facebook Ads',
          <FacebookAdsIcon />,
          memoizedMetrics.facebookMetrics,
          facebookData.loading,
        )}
        {databaseData &&
          renderMetricsDropdown(
            ConnectorType.DATABASE,
            'DB Database',
            <div
              style={{
                width: '20px',
                height: '20px',
                backgroundColor: '#1976d2',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold',
              }}
            >
              DB
            </div>,
            memoizedMetrics.databaseMetrics,
            databaseData.loading,
            databaseData.metrics.length,
          )}
      </CollapsesContainer>
    </MetricsCard>
  );
};
